import { Link } from 'react-router-dom';
import { AlertCircle, ArrowUpRight, MessageCircle, PackageCheck, PencilLine } from 'lucide-react';
import { saleDateLabel } from '@klothiq/domain';
import { deliveryLabel, type DeliveryRow } from '@/lib/deliveries';
import { DeliveryBadge, DeliveryMethod } from './DeliveryPresentation';
import { deliveryButton } from './DeliveryUpdateDialog';

export default function DeliveryShipmentRow({
  row,
  selected,
  selectionDisabled,
  onToggle,
  onUpdate,
  onWhatsApp,
}: {
  row: DeliveryRow;
  selected: boolean;
  selectionDisabled: boolean;
  onToggle: () => void;
  onUpdate?: () => void;
  onWhatsApp?: () => void;
}) {
  const local = row.fulfillment_method === 'local_delivery';
  const alert = row.packing_requires_review || !!row.issue;
  return (
    <li
      className={`group rounded-2xl border transition-colors ${selected ? 'border-primary/50 bg-primary/[0.04] ring-1 ring-primary/10' : 'border-border bg-card hover:border-primary/25'}`}
    >
      <div className="grid min-w-0 gap-3 p-3 sm:p-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,.85fr)_auto] xl:items-center xl:gap-5">
        <div className="flex min-w-0 items-start gap-2">
          <label className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg focus-within:ring-2 focus-within:ring-ring">
            <span className="sr-only">Select {row.sale_number}</span>
            <input
              type="checkbox"
              className="h-[18px] w-[18px] accent-primary"
              checked={selected}
              disabled={selectionDisabled}
              onChange={onToggle}
            />
          </label>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <Link
                className="inline-flex min-h-11 min-w-0 flex-col justify-center gap-0.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                to={`/deliveries/${row.sale_id}`}
              >
                <span className="break-all text-xs font-semibold text-cyan-700 dark:text-primary">
                  {row.sale_number}
                </span>
                <span className="break-words text-sm font-semibold text-foreground">
                  {row.customer_name || 'Customer name not recorded'}
                </span>
              </Link>
              <span className="mt-1 shrink-0 xl:hidden">
                <DeliveryBadge stage={row.stage} />
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              <DeliveryMethod method={row.fulfillment_method} />
              <span aria-hidden="true" className="text-xs text-muted-foreground/50">
                ·
              </span>
              <span className="text-xs text-muted-foreground">{saleDateLabel(row.sale_date)}</span>
            </div>
          </div>
        </div>
        <div className="min-w-0 border-t border-border/60 pt-3 xl:border-0 xl:pt-0">
          <p className="text-[10px] font-semibold uppercase tracking-[.12em] text-muted-foreground xl:hidden">
            Shipment
          </p>
          <p className="mt-1 break-words text-sm font-medium xl:mt-0">
            {local
              ? 'Your delivery team'
              : row.courier_name ||
                (row.stage ? 'Courier not assigned' : 'Choose a delivery method')}
          </p>
          <p
            className={`mt-1 break-all text-xs ${row.tracking_number ? 'font-mono text-foreground/80' : 'text-muted-foreground'}`}
          >
            {row.tracking_number ||
              (local
                ? 'Team delivery · no AWB required'
                : row.stage === 'delivered'
                  ? 'Tracking not recorded'
                  : 'Add tracking when dispatching')}
          </p>
          {alert && (
            <div className="mt-2 space-y-1 xl:hidden">
              {row.packing_requires_review && (
                <p className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                  <PackageCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  Packing needs recheck
                </p>
              )}
              {row.issue && (
                <p className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {deliveryLabel(row.issue)}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="hidden min-w-0 space-y-2 xl:block">
          <DeliveryBadge stage={row.stage} />
          {row.packing_requires_review && (
            <p className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
              <PackageCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Packing needs recheck
            </p>
          )}
          {row.issue && (
            <p className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {deliveryLabel(row.issue)}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-3 xl:justify-end xl:border-0 xl:pt-0">
          <Link
            className={`${deliveryButton} bg-muted/30 text-xs xl:h-11 xl:w-11 xl:px-0`}
            title={row.stage ? 'View delivery' : 'Set delivery method'}
            to={`/deliveries/${row.sale_id}`}
          >
            <span className="xl:sr-only">
              {row.stage ? 'View delivery' : 'Set delivery method'}
            </span>
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          </Link>
          <div className="flex gap-1">
            {onUpdate && (
              <button
                type="button"
                onClick={onUpdate}
                aria-label={`Update ${row.sale_number}`}
                title="Update delivery"
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition-colors hover:border-primary/30 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <PencilLine className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {onWhatsApp && (
              <button
                type="button"
                onClick={onWhatsApp}
                aria-label={`WhatsApp update for ${row.sale_number}`}
                title="WhatsApp update"
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-600/20 bg-emerald-500/5 text-emerald-700 transition-colors hover:bg-emerald-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-emerald-300"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
